import type { UploadField } from "@overmap-ai/forms"
import type { ReactNode } from "react"
import type { NumberRange } from "src/typings"

import {
	NumberEqualsFilterModifier,
	NumberGreaterThanFilterModifier,
	NumberGreaterThanOrEqualsFilterModifier,
	NumberLessThanFilterModifier,
	NumberLessThanOrEqualsFilterModifier,
	NumberNotEqualsFilterModifier,
	NumberRangeInFilterModifier,
	NumberRangeNotInFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { UploadFieldFilterCell } from "./UploadFieldFilterCell"

// Callers supply the attachment count, including 0 for an empty upload field.
type UploadFieldValue = number
type UploadFilterValue = number | NumberRange

export class UploadFieldFilterDef extends BaseFilterDef<UploadFieldValue, UploadFilterValue> {
	readonly id: string
	readonly defaultFilterValue: UploadFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: UploadField

	constructor(field: UploadField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NumberEqualsFilterModifier,
			isValueValid: (value: UploadFieldValue): value is number => Number.isInteger(value) && value >= 0,
			isFilterValueValid: (filterValue: UploadFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		notEquals: createFilterModifierConfig({
			modifier: NumberNotEqualsFilterModifier,
			isValueValid: (value: UploadFieldValue): value is number => Number.isInteger(value) && value >= 0,
			isFilterValueValid: (filterValue: UploadFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		lessThan: createFilterModifierConfig({
			modifier: NumberLessThanFilterModifier,
			isValueValid: (value: UploadFieldValue): value is number =>
				value !== null && Number.isInteger(value) && value >= 0,
			isFilterValueValid: (filterValue: UploadFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		lessThanOrEquals: createFilterModifierConfig({
			modifier: NumberLessThanOrEqualsFilterModifier,
			isValueValid: (value: UploadFieldValue): value is number =>
				value !== null && Number.isInteger(value) && value >= 0,
			isFilterValueValid: (filterValue: UploadFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		greaterThan: createFilterModifierConfig({
			modifier: NumberGreaterThanFilterModifier,
			isValueValid: (value: UploadFieldValue): value is number =>
				value !== null && Number.isInteger(value) && value >= 0,
			isFilterValueValid: (filterValue: UploadFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		greaterThanOrEquals: createFilterModifierConfig({
			modifier: NumberGreaterThanOrEqualsFilterModifier,
			isValueValid: (value: UploadFieldValue): value is number =>
				value !== null && Number.isInteger(value) && value >= 0,
			isFilterValueValid: (filterValue: UploadFilterValue): filterValue is number =>
				typeof filterValue === "number",
		}),
		inRange: createFilterModifierConfig({
			modifier: NumberRangeInFilterModifier,
			isValueValid: (value: UploadFieldValue): value is number =>
				value !== null && Number.isInteger(value) && value >= 0,
			isFilterValueValid: (filterValue: UploadFilterValue): filterValue is NumberRange =>
				typeof filterValue === "object" && filterValue !== null,
		}),
		notInRange: createFilterModifierConfig({
			modifier: NumberRangeNotInFilterModifier,
			isValueValid: (value: UploadFieldValue): value is number =>
				value !== null && Number.isInteger(value) && value >= 0,
			isFilterValueValid: (filterValue: UploadFilterValue): filterValue is NumberRange =>
				typeof filterValue === "object" && filterValue !== null,
		}),
	}

	render(props: FilterDefRenderProps<UploadFieldFilterDef>): ReactNode {
		return <UploadFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
